import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { InvoiceType } from "../api/inventoryInvoicesApi";
import { useAppState } from "../context/AppStateContext";
import { useToast } from "../context/ToastContext";
import { downloadPdfFromHtml, warmPoPdfLogoCache } from "../utils/downloadPdfFromHtml";
import {
  buildSampleInvoicePreviewDocument,
  buildSampleInvoicePreviewHtml,
} from "../utils/issueInvoicePdf";
import {
  defaultInvoiceFormatSettings,
  documentTitleForInvoiceType,
  gstEditorRowsToSaved,
  loadInvoiceFormatSettings,
  normalizeInvoiceFormatSettings,
  saveInvoiceFormatSettings,
  savedGstToEditorRows,
  type InvoiceFormatSettings,
  type InvoiceTypeFormatBlock,
  type SubcontractorBillLineSetting,
} from "../utils/invoiceFormatSettings";
import { buildSubcontractorBillLinesFromMasters, milestoneBillLines, timelineBillLines } from "../utils/subcontractorBillFormatLines";
import { buildLabourBillLinesFromMasters, copySubcontractorRatesToLabourLines, milestoneLabourBillLines } from "../utils/labourBillFormatLines";

type GstRow = { id: string; label: string; rateInput: string };

const INVOICE_TYPE_TABS: { id: InvoiceType; label: string }[] = [
  { id: "material", label: "Material" },
  { id: "subcontractor", label: "Subcontractor" },
  { id: "labour", label: "Labour" },
];

function documentTitleForType(s: InvoiceFormatSettings, type: InvoiceType): string {
  return documentTitleForInvoiceType(s, type);
}

function cloneTypeFormats(initial: InvoiceFormatSettings): Record<InvoiceType, InvoiceTypeFormatBlock> {
  return {
    material: { ...initial.material, gstRows: initial.material.gstRows.map((r) => ({ ...r })) },
    subcontractor: {
      ...initial.subcontractor,
      gstRows: initial.subcontractor.gstRows.map((r) => ({ ...r })),
    },
    labour: { ...initial.labour, gstRows: initial.labour.gstRows.map((r) => ({ ...r })) },
  };
}

function setDocumentTitleForType(
  type: InvoiceType,
  value: string,
  setters: {
    setMaterial: (v: string) => void;
    setSubcontractor: (v: string) => void;
    setLabour: (v: string) => void;
  },
): void {
  if (type === "material") setters.setMaterial(value);
  else if (type === "subcontractor") setters.setSubcontractor(value);
  else setters.setLabour(value);
}

export function InvoiceFormatSettingsPage() {
  const { showToast } = useToast();
  const { milestoneTemplates, constructionTimelinePhases } = useAppState();
  const initial = useMemo(() => loadInvoiceFormatSettings(), []);

  const [documentTitleMaterial, setDocumentTitleMaterial] = useState(initial.documentTitleMaterial);
  const [documentTitleSubcontractor, setDocumentTitleSubcontractor] = useState(initial.documentTitleSubcontractor);
  const [documentTitleLabour, setDocumentTitleLabour] = useState(initial.documentTitleLabour);
  const [letterheadTagline, setLetterheadTagline] = useState(initial.letterheadTagline);
  const [logoPrimaryUrl, setLogoPrimaryUrl] = useState(initial.logoPrimaryUrl);
  const [logoSecondaryUrl, setLogoSecondaryUrl] = useState(initial.logoSecondaryUrl);
  const [logoPrimaryHeightPx, setLogoPrimaryHeightPx] = useState(initial.logoPrimaryHeightPx);
  const [logoSecondaryHeightPx, setLogoSecondaryHeightPx] = useState(initial.logoSecondaryHeightPx);
  const [accentColorHex, setAccentColorHex] = useState(initial.accentColorHex);
  const [typeFormats, setTypeFormats] = useState(() => cloneTypeFormats(initial));
  const [subcontractorBillLines, setSubcontractorBillLines] = useState<SubcontractorBillLineSetting[]>(
    () => initial.subcontractorBillLines,
  );
  const [labourBillLines, setLabourBillLines] = useState<SubcontractorBillLineSetting[]>(() => initial.labourBillLines);
  const [activeTab, setActiveTab] = useState<"format" | "preview">("format");
  const [invoiceTypeTab, setInvoiceTypeTab] = useState<InvoiceType>("material");
  const [samplePdfBusy, setSamplePdfBusy] = useState(false);

  const activeFormat = typeFormats[invoiceTypeTab];
  const gstRows = useMemo(() => savedGstToEditorRows(activeFormat.gstRows), [activeFormat.gstRows]);

  function patchActiveFormat(patch: Partial<InvoiceTypeFormatBlock>) {
    setTypeFormats((prev) => ({
      ...prev,
      [invoiceTypeTab]: { ...prev[invoiceTypeTab], ...patch },
    }));
  }

  function setActiveGstRows(updater: (rows: GstRow[]) => GstRow[]) {
    setTypeFormats((prev) => {
      const cur = prev[invoiceTypeTab];
      const editorRows = savedGstToEditorRows(cur.gstRows);
      return {
        ...prev,
        [invoiceTypeTab]: { ...cur, gstRows: gstEditorRowsToSaved(updater(editorRows)) },
      };
    });
  }

  function buildDraft(): InvoiceFormatSettings {
    return normalizeInvoiceFormatSettings({
      version: 2,
      documentTitleMaterial,
      documentTitleSubcontractor,
      documentTitleLabour,
      letterheadTagline,
      logoPrimaryUrl,
      logoSecondaryUrl,
      logoPrimaryHeightPx,
      logoSecondaryHeightPx,
      accentColorHex,
      material: typeFormats.material,
      subcontractor: typeFormats.subcontractor,
      labour: typeFormats.labour,
      subcontractorBillLines,
      labourBillLines,
    });
  }

  useEffect(() => {
    setSubcontractorBillLines((prev) =>
      buildSubcontractorBillLinesFromMasters(milestoneTemplates, constructionTimelinePhases, prev),
    );
  }, [milestoneTemplates, constructionTimelinePhases]);

  useEffect(() => {
    setLabourBillLines((prev) =>
      buildLabourBillLinesFromMasters(
        milestoneTemplates,
        constructionTimelinePhases,
        prev,
        subcontractorBillLines,
      ),
    );
  }, [milestoneTemplates, constructionTimelinePhases, subcontractorBillLines]);

  const draftSettings = useMemo(
    () => buildDraft(),
    [
      documentTitleMaterial,
      documentTitleSubcontractor,
      documentTitleLabour,
      letterheadTagline,
      logoPrimaryUrl,
      logoSecondaryUrl,
      logoPrimaryHeightPx,
      logoSecondaryHeightPx,
      accentColorHex,
      typeFormats,
      subcontractorBillLines,
      labourBillLines,
    ],
  );

  const subcontractorMilestoneRows = useMemo(
    () => milestoneBillLines(subcontractorBillLines),
    [subcontractorBillLines],
  );
  const subcontractorTimelineRows = useMemo(
    () => timelineBillLines(subcontractorBillLines),
    [subcontractorBillLines],
  );
  const labourTimelineRows = useMemo(() => timelineBillLines(labourBillLines), [labourBillLines]);
  const labourMilestoneRows = useMemo(() => milestoneLabourBillLines(labourBillLines), [labourBillLines]);

  function patchSubcontractorBillLine(
    itemId: string,
    patch: Partial<Pick<SubcontractorBillLineSetting, "qty" | "unit" | "rate" | "hsnCode" | "lineLabel">>,
  ) {
    setSubcontractorBillLines((rows) =>
      rows.map((r) => (r.milestoneTemplateItemId === itemId ? { ...r, ...patch } : r)),
    );
  }

  function patchLabourBillLine(
    itemId: string,
    patch: Partial<Pick<SubcontractorBillLineSetting, "qty" | "unit" | "rate" | "hsnCode" | "lineLabel">>,
  ) {
    setLabourBillLines((rows) =>
      rows.map((r) => (r.milestoneTemplateItemId === itemId ? { ...r, ...patch } : r)),
    );
  }

  const previewHtml = useMemo(
    () => buildSampleInvoicePreviewHtml(draftSettings, invoiceTypeTab),
    [draftSettings, invoiceTypeTab],
  );

  const samplePdfDoc = useMemo(
    () => buildSampleInvoicePreviewDocument(draftSettings, invoiceTypeTab),
    [draftSettings, invoiceTypeTab],
  );

  useEffect(() => {
    void warmPoPdfLogoCache([logoPrimaryUrl, logoSecondaryUrl]);
  }, [logoPrimaryUrl, logoSecondaryUrl]);

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>, setter: (v: string) => void) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (typeof ev.target?.result === "string") setter(ev.target.result);
    };
    reader.readAsDataURL(file);
  }

  function handleSave() {
    saveInvoiceFormatSettings(buildDraft());
    showToast("Invoice format saved. Downloads will use these defaults.", "success");
  }

  function handleReset() {
    const d = defaultInvoiceFormatSettings();
    setDocumentTitleMaterial(d.documentTitleMaterial);
    setDocumentTitleSubcontractor(d.documentTitleSubcontractor);
    setDocumentTitleLabour(d.documentTitleLabour);
    setLetterheadTagline(d.letterheadTagline);
    setLogoPrimaryUrl(d.logoPrimaryUrl);
    setLogoSecondaryUrl(d.logoSecondaryUrl);
    setLogoPrimaryHeightPx(d.logoPrimaryHeightPx);
    setLogoSecondaryHeightPx(d.logoSecondaryHeightPx);
    setAccentColorHex(d.accentColorHex);
    setTypeFormats(cloneTypeFormats(d));
    setSubcontractorBillLines(
      buildSubcontractorBillLinesFromMasters(milestoneTemplates, constructionTimelinePhases, d.subcontractorBillLines),
    );
    setLabourBillLines(
      buildLabourBillLinesFromMasters(
        milestoneTemplates,
        constructionTimelinePhases,
        d.labourBillLines,
        d.subcontractorBillLines,
      ),
    );
    showToast("Form reset to factory defaults (not saved yet).", "info");
  }

  const titleSetters = {
    setMaterial: setDocumentTitleMaterial,
    setSubcontractor: setDocumentTitleSubcontractor,
    setLabour: setDocumentTitleLabour,
  };

  return (
    <div
      className="workspace"
      style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 4rem)", background: "#f1f5f9" }}
    >
      <div style={{ width: "100%", padding: "1rem", flex: "0 0 auto", display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "0.75rem" }}>
        <div style={{ display: "flex", gap: "0.25rem", background: "#cbd5e1", padding: "0.3rem", borderRadius: "8px" }}>
          <button
            type="button"
            className={`btn ${activeTab === "format" ? "btn--primary" : ""}`}
            style={
              activeTab === "format"
                ? { boxShadow: "0 1px 3px rgba(0,0,0,0.1)", minWidth: "140px" }
                : { background: "transparent", border: "none", color: "#334155", minWidth: "140px", fontWeight: 500 }
            }
            onClick={() => setActiveTab("format")}
          >
            Format settings
          </button>
          <button
            type="button"
            className={`btn ${activeTab === "preview" ? "btn--primary" : ""}`}
            style={
              activeTab === "preview"
                ? { boxShadow: "0 1px 3px rgba(0,0,0,0.1)", minWidth: "140px" }
                : { background: "transparent", border: "none", color: "#334155", minWidth: "140px", fontWeight: 500 }
            }
            onClick={() => setActiveTab("preview")}
          >
            Live preview
          </button>
        </div>
        <div style={{ display: "flex", gap: "0.25rem", background: "#e2e8f0", padding: "0.3rem", borderRadius: "8px" }}>
          {INVOICE_TYPE_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`btn ${invoiceTypeTab === t.id ? "btn--primary" : ""}`}
              style={
                invoiceTypeTab === t.id
                  ? { boxShadow: "0 1px 3px rgba(0,0,0,0.1)", minWidth: "120px" }
                  : { background: "transparent", border: "none", color: "#334155", minWidth: "120px", fontWeight: 500 }
              }
              onClick={() => setInvoiceTypeTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          gap: "1rem",
          padding: "0 1rem 1rem 1rem",
          justifyContent: "center",
        }}
      >
        {activeTab === "format" ? (
          <div
            className="card card--gov card--elevated"
            style={{
              flex: 1,
              maxWidth: "1200px",
              overflowY: "auto",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ padding: "1.5rem" }}>
              <p className="section-lead section-lead--tight text-muted" style={{ marginTop: 0 }}>
                Letterhead and logos are shared. Use the Material / Subcontractor / Labour tabs to set document title,
                Bill From / Bill To, payment terms, footer, GST, and aggregate-qty defaults for each invoice type.
              </p>

              <h3 className="form-section-title">{INVOICE_TYPE_TABS.find((t) => t.id === invoiceTypeTab)?.label} invoice title</h3>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Document title on PDF</span>
                <input
                  className="form-control"
                  value={documentTitleForType(draftSettings, invoiceTypeTab)}
                  onChange={(e) => setDocumentTitleForType(invoiceTypeTab, e.target.value, titleSetters)}
                  placeholder="TAX INVOICE"
                />
              </label>

              <h3 className="form-section-title" style={{ marginTop: "1.25rem" }}>
                Shared letterhead
              </h3>
              <div className="inv-outward-issue__grid">
                <label className="inv-outward-issue__field inv-outward-issue__field--full">
                  <span className="inv-outward-issue__label">Letterhead tagline</span>
                  <input
                    className="form-control"
                    value={letterheadTagline}
                    onChange={(e) => setLetterheadTagline(e.target.value)}
                    placeholder="Programme / department name"
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                  <span className="inv-outward-issue__label">Accent colour</span>
                  <input
                    type="color"
                    className="form-control"
                    style={{ maxWidth: "72px", height: "38px", padding: "4px" }}
                    value={/^#[0-9A-Fa-f]{6}$/.test(accentColorHex.trim()) ? accentColorHex.trim() : "#16365f"}
                    onChange={(e) => setAccentColorHex(e.target.value)}
                    aria-label="Accent colour"
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                  <span className="inv-outward-issue__label">Accent hex</span>
                  <input
                    className="form-control mono"
                    value={accentColorHex}
                    onChange={(e) => setAccentColorHex(e.target.value)}
                    placeholder="#16365f"
                  />
                </label>
                <div
                  className="inv-outward-issue__field inv-outward-issue__field--full"
                  style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "1rem" }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <label className="inv-outward-issue__field">
                      <span className="inv-outward-issue__label">Primary logo</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        style={{ padding: "0.4rem 0.6rem" }}
                        onChange={(e) => handleLogoUpload(e, setLogoPrimaryUrl)}
                      />
                    </label>
                    <input
                      className="form-control mono"
                      value={logoPrimaryUrl}
                      onChange={(e) => setLogoPrimaryUrl(e.target.value)}
                      placeholder="/branding/Telangana-LOGO.png"
                    />
                  </div>
                  {logoPrimaryUrl ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed var(--border)",
                        borderRadius: "8px",
                        padding: "0.5rem",
                        background: "#f8fafc",
                      }}
                    >
                      <img
                        src={logoPrimaryUrl}
                        alt="Primary preview"
                        style={{ maxHeight: "80px", maxWidth: "100%", objectFit: "contain" }}
                      />
                    </div>
                  ) : null}
                </div>
                <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                  <span className="inv-outward-issue__label">Primary logo height (px)</span>
                  <input
                    type="number"
                    min={28}
                    max={160}
                    className="form-control mono"
                    value={Number.isFinite(logoPrimaryHeightPx) ? logoPrimaryHeightPx : ""}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setLogoPrimaryHeightPx(Number.isFinite(v) ? v : defaultInvoiceFormatSettings().logoPrimaryHeightPx);
                    }}
                  />
                </label>
                <div
                  className="inv-outward-issue__field inv-outward-issue__field--full"
                  style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "1rem" }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <label className="inv-outward-issue__field">
                      <span className="inv-outward-issue__label">Secondary logo (optional)</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        style={{ padding: "0.4rem 0.6rem" }}
                        onChange={(e) => handleLogoUpload(e, setLogoSecondaryUrl)}
                      />
                    </label>
                    <input
                      className="form-control mono"
                      value={logoSecondaryUrl}
                      onChange={(e) => setLogoSecondaryUrl(e.target.value)}
                      placeholder="/branding/your-company.png"
                    />
                  </div>
                  {logoSecondaryUrl ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed var(--border)",
                        borderRadius: "8px",
                        padding: "0.5rem",
                        background: "#f8fafc",
                      }}
                    >
                      <img
                        src={logoSecondaryUrl}
                        alt="Secondary preview"
                        style={{ maxHeight: "80px", maxWidth: "100%", objectFit: "contain" }}
                      />
                    </div>
                  ) : null}
                </div>
                <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                  <span className="inv-outward-issue__label">Secondary logo height (px)</span>
                  <input
                    type="number"
                    min={28}
                    max={160}
                    className="form-control mono"
                    value={Number.isFinite(logoSecondaryHeightPx) ? logoSecondaryHeightPx : ""}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setLogoSecondaryHeightPx(
                        Number.isFinite(v) ? v : defaultInvoiceFormatSettings().logoSecondaryHeightPx,
                      );
                    }}
                  />
                </label>
              </div>

              <h3 className="form-section-title" style={{ marginTop: "1.25rem" }}>
                {INVOICE_TYPE_TABS.find((t) => t.id === invoiceTypeTab)?.label} — Bill From / Bill To
              </h3>

              {invoiceTypeTab === "subcontractor" ? (
                <div style={{ marginBottom: "1.25rem" }}>
                  <h3 className="form-section-title">Subcontractor bill lines</h3>
                  <p className="text-muted" style={{ fontSize: "0.88rem", marginTop: "-0.35rem" }}>
                    Configure milestone-wise and timeline-wise rates separately. Milestones come from{" "}
                    <Link to="/milestone-master">Milestone master</Link>; timeline levels from{" "}
                    <Link to="/construction-timeline-master">Timeline master</Link>.
                  </p>

                  <h4 className="form-section-title" style={{ marginTop: "1rem", fontSize: "0.95rem" }}>
                    Milestone-wise bill lines
                  </h4>
                  {subcontractorMilestoneRows.length === 0 ? (
                    <p className="text-muted">No milestones in master yet.</p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="data-table" style={{ marginTop: "0.35rem" }}>
                        <thead>
                          <tr>
                            <th>Milestone</th>
                            <th>Bill line</th>
                            <th>Qty</th>
                            <th>Unit</th>
                            <th>Rate (₹)</th>
                            <th>HSN/SAC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subcontractorMilestoneRows.map((row) => (
                            <tr key={row.milestoneTemplateItemId}>
                              <td>{row.milestoneTitle}</td>
                              <td>{row.lineLabel}</td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "5rem" }}
                                  value={String(row.qty)}
                                  onChange={(e) =>
                                    patchSubcontractorBillLine(row.milestoneTemplateItemId, {
                                      qty: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control"
                                  style={{ maxWidth: "5rem" }}
                                  value={row.unit}
                                  onChange={(e) =>
                                    patchSubcontractorBillLine(row.milestoneTemplateItemId, { unit: e.target.value })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "7rem" }}
                                  value={String(row.rate)}
                                  onChange={(e) =>
                                    patchSubcontractorBillLine(row.milestoneTemplateItemId, {
                                      rate: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "7rem" }}
                                  value={row.hsnCode}
                                  onChange={(e) =>
                                    patchSubcontractorBillLine(row.milestoneTemplateItemId, {
                                      hsnCode: e.target.value.toUpperCase(),
                                    })
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <h4 className="form-section-title" style={{ marginTop: "1.25rem", fontSize: "0.95rem" }}>
                    Timeline-wise bill lines
                  </h4>
                  {subcontractorTimelineRows.length === 0 ? (
                    <p className="text-muted">No timeline levels in master yet.</p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="data-table" style={{ marginTop: "0.35rem" }}>
                        <thead>
                          <tr>
                            <th>Timeline level</th>
                            <th>Bill line</th>
                            <th>Qty</th>
                            <th>Unit</th>
                            <th>Rate (₹)</th>
                            <th>HSN/SAC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subcontractorTimelineRows.map((row) => (
                            <tr key={row.milestoneTemplateItemId}>
                              <td>{row.timelinePhaseTitles[0] || "—"}</td>
                              <td>{row.lineLabel}</td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "5rem" }}
                                  value={String(row.qty)}
                                  onChange={(e) =>
                                    patchSubcontractorBillLine(row.milestoneTemplateItemId, {
                                      qty: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control"
                                  style={{ maxWidth: "5rem" }}
                                  value={row.unit}
                                  onChange={(e) =>
                                    patchSubcontractorBillLine(row.milestoneTemplateItemId, { unit: e.target.value })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "7rem" }}
                                  value={String(row.rate)}
                                  onChange={(e) =>
                                    patchSubcontractorBillLine(row.milestoneTemplateItemId, {
                                      rate: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "7rem" }}
                                  value={row.hsnCode}
                                  onChange={(e) =>
                                    patchSubcontractorBillLine(row.milestoneTemplateItemId, {
                                      hsnCode: e.target.value.toUpperCase(),
                                    })
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn--tg-secondary btn--sm"
                    style={{ marginTop: "0.75rem" }}
                    onClick={() =>
                      setSubcontractorBillLines((prev) =>
                        buildSubcontractorBillLinesFromMasters(milestoneTemplates, constructionTimelinePhases, prev),
                      )
                    }
                  >
                    Refresh from masters
                  </button>
                </div>
              ) : invoiceTypeTab === "labour" ? (
                <div style={{ marginBottom: "1.25rem" }}>
                  <h3 className="form-section-title">Labour bill lines</h3>
                  <p className="text-muted" style={{ fontSize: "0.88rem", marginTop: "-0.35rem" }}>
                    Configure milestone-wise and timeline-wise labour rates separately (same setup as subcontractor).
                    Milestones come from <Link to="/milestone-master">Milestone master</Link>; timeline levels from{" "}
                    <Link to="/construction-timeline-master">Timeline master</Link>. Bill lines use{" "}
                    <strong>Labour Charges</strong> (e.g. Basement Level 1 — Labour Charges).
                  </p>

                  <h4 className="form-section-title" style={{ marginTop: "1rem", fontSize: "0.95rem" }}>
                    Milestone-wise bill lines
                  </h4>
                  {labourMilestoneRows.length === 0 ? (
                    <p className="text-muted">No milestones in master yet.</p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="data-table" style={{ marginTop: "0.35rem" }}>
                        <thead>
                          <tr>
                            <th>Milestone</th>
                            <th>Bill line</th>
                            <th>Qty</th>
                            <th>Unit</th>
                            <th>Rate (₹)</th>
                            <th>HSN/SAC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {labourMilestoneRows.map((row) => (
                            <tr key={row.milestoneTemplateItemId}>
                              <td>{row.milestoneTitle}</td>
                              <td>{row.lineLabel}</td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "5rem" }}
                                  value={String(row.qty)}
                                  onChange={(e) =>
                                    patchLabourBillLine(row.milestoneTemplateItemId, {
                                      qty: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control"
                                  style={{ maxWidth: "5rem" }}
                                  value={row.unit}
                                  onChange={(e) =>
                                    patchLabourBillLine(row.milestoneTemplateItemId, { unit: e.target.value })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "7rem" }}
                                  value={String(row.rate)}
                                  onChange={(e) =>
                                    patchLabourBillLine(row.milestoneTemplateItemId, {
                                      rate: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "7rem" }}
                                  value={row.hsnCode}
                                  onChange={(e) =>
                                    patchLabourBillLine(row.milestoneTemplateItemId, {
                                      hsnCode: e.target.value.toUpperCase(),
                                    })
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <h4 className="form-section-title" style={{ marginTop: "1.25rem", fontSize: "0.95rem" }}>
                    Timeline-wise bill lines
                  </h4>
                  {labourTimelineRows.length === 0 ? (
                    <p className="text-muted">No timeline levels in master yet.</p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="data-table" style={{ marginTop: "0.35rem" }}>
                        <thead>
                          <tr>
                            <th>Timeline level</th>
                            <th>Bill line</th>
                            <th>Qty</th>
                            <th>Unit</th>
                            <th>Rate (₹)</th>
                            <th>HSN/SAC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {labourTimelineRows.map((row) => (
                            <tr key={row.milestoneTemplateItemId}>
                              <td>{row.timelinePhaseTitles[0] || "—"}</td>
                              <td>{row.lineLabel}</td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "5rem" }}
                                  value={String(row.qty)}
                                  onChange={(e) =>
                                    patchLabourBillLine(row.milestoneTemplateItemId, {
                                      qty: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control"
                                  style={{ maxWidth: "5rem" }}
                                  value={row.unit}
                                  onChange={(e) =>
                                    patchLabourBillLine(row.milestoneTemplateItemId, { unit: e.target.value })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "7rem" }}
                                  value={String(row.rate)}
                                  onChange={(e) =>
                                    patchLabourBillLine(row.milestoneTemplateItemId, {
                                      rate: Math.max(0, Number(e.target.value) || 0),
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control mono"
                                  style={{ maxWidth: "7rem" }}
                                  value={row.hsnCode}
                                  onChange={(e) =>
                                    patchLabourBillLine(row.milestoneTemplateItemId, {
                                      hsnCode: e.target.value.toUpperCase(),
                                    })
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="toolbar-row" style={{ marginTop: "0.75rem", gap: "0.5rem" }}>
                    <button
                      type="button"
                      className="btn btn--tg-secondary btn--sm"
                      onClick={() =>
                        setLabourBillLines((prev) =>
                          buildLabourBillLinesFromMasters(
                            milestoneTemplates,
                            constructionTimelinePhases,
                            prev,
                            subcontractorBillLines,
                          ),
                        )
                      }
                    >
                      Refresh from masters
                    </button>
                    <button
                      type="button"
                      className="btn btn--tg-secondary btn--sm"
                      onClick={() =>
                        setLabourBillLines((prev) => copySubcontractorRatesToLabourLines(prev, subcontractorBillLines))
                      }
                      disabled={!subcontractorBillLines.some((r) => r.rate > 0)}
                    >
                      Copy rates from subcontractor
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="inv-outward-issue__grid">
                <label className="inv-outward-issue__field inv-outward-issue__field--full">
                  <span className="inv-outward-issue__label">Bill From name</span>
                  <input
                    className="form-control"
                    value={activeFormat.billFromName}
                    onChange={(e) => patchActiveFormat({ billFromName: e.target.value })}
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--full">
                  <span className="inv-outward-issue__label">Bill From address</span>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={activeFormat.billFromAddress}
                    onChange={(e) => patchActiveFormat({ billFromAddress: e.target.value })}
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                  <span className="inv-outward-issue__label">Bill From GSTIN</span>
                  <input
                    className="form-control mono"
                    value={activeFormat.billFromGstin}
                    onChange={(e) => patchActiveFormat({ billFromGstin: e.target.value.toUpperCase() })}
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--full">
                  <span className="inv-outward-issue__label">Bill To name</span>
                  <input
                    className="form-control"
                    value={activeFormat.billToName}
                    onChange={(e) => patchActiveFormat({ billToName: e.target.value })}
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--full">
                  <span className="inv-outward-issue__label">Bill To address</span>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={activeFormat.billToAddress}
                    onChange={(e) => patchActiveFormat({ billToAddress: e.target.value })}
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                  <span className="inv-outward-issue__label">Bill To GSTIN</span>
                  <input
                    className="form-control mono"
                    value={activeFormat.billToGstin}
                    onChange={(e) => patchActiveFormat({ billToGstin: e.target.value.toUpperCase() })}
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--full">
                  <span className="inv-outward-issue__label">Payment terms</span>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={activeFormat.paymentTerms}
                    onChange={(e) => patchActiveFormat({ paymentTerms: e.target.value })}
                  />
                </label>
                <label className="inv-outward-issue__field inv-outward-issue__field--full">
                  <span className="inv-outward-issue__label">Footer note</span>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={activeFormat.footerNote}
                    onChange={(e) => patchActiveFormat({ footerNote: e.target.value })}
                  />
                </label>
              </div>

              <label className="text-muted" style={{ display: "flex", gap: "0.4rem", alignItems: "center", marginTop: "0.75rem" }}>
                <input
                  type="checkbox"
                  checked={activeFormat.aggregateQtyDefault}
                  onChange={(e) => patchActiveFormat({ aggregateQtyDefault: e.target.checked })}
                />
                Aggregate quantities on PDF main table (beneficiary detail in annexure)
              </label>
            </div>

            <div style={{ padding: "0 1.5rem 1.5rem" }}>
              <h3 className="form-section-title">
                {INVOICE_TYPE_TABS.find((t) => t.id === invoiceTypeTab)?.label} — default GST rows
              </h3>
              <p className="text-muted" style={{ fontSize: "0.88rem", marginTop: "-0.35rem" }}>
                Applied on each invoice download; tax is computed on taxable value (qty × rate).
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem" }}>
                {gstRows.map((row) => (
                  <div key={row.id} style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                    <input
                      className="form-control"
                      style={{ width: "120px" }}
                      value={row.label}
                      onChange={(e) =>
                        setActiveGstRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, label: e.target.value } : r)))
                      }
                      aria-label="GST label"
                    />
                    <input
                      className="form-control mono"
                      style={{ width: "88px" }}
                      value={row.rateInput}
                      onChange={(e) =>
                        setActiveGstRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, rateInput: e.target.value } : r)))
                      }
                      aria-label="GST percent"
                    />
                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                      %
                    </span>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => setActiveGstRows((rs) => rs.filter((r) => r.id !== row.id))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn--tg-secondary btn--sm"
                onClick={() => setActiveGstRows((rs) => [...rs, { id: `gst-${Date.now()}`, label: "IGST", rateInput: "18" }])}
              >
                Add GST row
              </button>
            </div>

            <div
              style={{
                position: "sticky",
                bottom: 0,
                background: "#fff",
                padding: "1rem 1.5rem",
                borderTop: "1px solid var(--border)",
                marginTop: "auto",
                zIndex: 10,
              }}
            >
              <div className="toolbar-row">
                <button type="button" className="btn btn--primary btn--tg btn--lg" onClick={handleSave}>
                  Save invoice format
                </button>
                <button type="button" className="btn btn--tg-secondary btn--lg" onClick={handleReset}>
                  Reset
                </button>
                <Link className="btn btn--tg-secondary btn--lg" to="/bills/inward">
                  Invoice register
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === "preview" ? (
          <div
            className="card card--gov card--elevated"
            style={{
              flex: 1,
              maxWidth: "1200px",
              margin: 0,
              padding: 0,
              overflowX: "auto",
              overflowY: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "1rem",
                borderBottom: "1px solid var(--border)",
                background: "#f8fafc",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.5rem",
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: "1rem", color: "var(--text)" }}>
                  Live preview — {INVOICE_TYPE_TABS.find((t) => t.id === invoiceTypeTab)?.label} invoice
                </h3>
                <p className="text-muted" style={{ margin: "0.2rem 0 0", fontSize: "0.85rem" }}>
                  Updates instantly with sample lines.
                </p>
              </div>
              <button
                type="button"
                className="btn btn--primary btn--tg btn--sm"
                disabled={samplePdfBusy}
                onClick={() => {
                  void (async () => {
                    setSamplePdfBusy(true);
                    try {
                      await downloadPdfFromHtml(
                        buildSampleInvoicePreviewHtml(draftSettings, invoiceTypeTab, { forPdfCapture: true }),
                        `INVOICE_SAMPLE_${invoiceTypeTab.toUpperCase()}`,
                        samplePdfDoc.allocations.length > 1
                          ? {
                              annexure: {
                                allocations: samplePdfDoc.allocations,
                                accentColorHex: samplePdfDoc.accentColorHex,
                                footerNote: samplePdfDoc.footerNote,
                              },
                            }
                          : undefined,
                      );
                      showToast("Sample invoice PDF downloaded.", "success");
                    } catch (e) {
                      showToast(e instanceof Error ? e.message : "Could not create PDF.", "error");
                    } finally {
                      setSamplePdfBusy(false);
                    }
                  })();
                }}
              >
                {samplePdfBusy ? "Creating PDF…" : "Download sample PDF"}
              </button>
            </div>
            <iframe
              srcDoc={previewHtml}
              style={{ flex: 1, width: "100%", minWidth: "800px", border: "none", background: "#cbd5e1" }}
              title="Live invoice preview"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
